import { useState } from 'react';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { ConfirmDialog, Dialog } from '@/ui/Dialog';
import { Field, Input, Textarea } from '@/ui/Field';
import { SkeletonTarjetas } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import {
  useBorrarPlantilla, useCrearPlantilla, useGuardarPlantilla, usePlantillas,
  type Plantilla,
} from './api';

/**
 * Plantillas.tsx — opción 5 del módulo 6: las plantillas de correo.
 *
 * ## El escapado a mano desaparece
 *
 * El formulario vanilla montaba sus campos así:
 *
 *     value="${(t.name||'').replace(/"/g,'&quot;')}"
 *
 * Escapado a mano, campo por campo, y solo para la comilla doble — el resto
 * del marcado pasaba. Y el `<textarea>` del cuerpo interpolaba
 * `${t.body_html_template || ''}` **sin escapar nada**, así que un
 * `</textarea>` dentro de la plantilla cerraba el campo y volcaba el resto
 * como marcado de la página. React no construye HTML: los valores son datos.
 *
 * ## El límite de 160 caracteres se dice, no se recorta en silencio
 *
 * El servidor trunca `body_push_template` a 160 (`notifications.py:225`). El
 * vanilla ponía `maxlength` y ya está; aquí se ve cuántos quedan, que es la
 * diferencia entre saber que te falta sitio y descubrir que te cortaron.
 *
 * ## Lo que NO se arregla aquí, y por qué
 *
 * `PUT /notifications/templates/{id}` recibe los cuatro campos como query
 * params, incluido el cuerpo HTML del correo. Está medido y documentado en
 * `api.ts`: es un cambio de contrato y va con su propio cambio, igual que
 * `motivo` en Novedades.
 */

const VARIABLES = [
  '{{nombre_corto}}', '{{nombre_completo}}', '{{empresa}}',
  '{{area}}', '{{cargo}}', '{{url_portal}}',
];

const TOPE_PUSH = 160;

type Campos = { name: string; subject_template: string; body_html_template: string; body_push_template: string };

const VACIA: Campos = { name: '', subject_template: '', body_html_template: '', body_push_template: '' };

export function Plantillas() {
  const { data, isLoading, isError, error, refetch } = usePlantillas();
  const [editando, setEditando] = useState<Plantilla | 'nueva' | null>(null);
  const [borrando, setBorrando] = useState<Plantilla | null>(null);
  const borrar = useBorrarPlantilla();

  if (isLoading) return <SkeletonTarjetas n={4} />;

  if (isError) {
    return (
      <Card className="p-4">
        <EstadoError err={error} que="las plantillas" onReintentar={() => refetch()} />
      </Card>
    );
  }

  const plantillas = data ?? [];

  return (
    <div className="max-w-3xl">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <p className="text-3 text-muted" aria-live="polite">
          {plantillas.length} plantilla{plantillas.length === 1 ? '' : 's'}
        </p>
        <Button size="sm" onClick={() => setEditando('nueva')}>
          <i className="bi bi-plus-circle" aria-hidden="true" /> Nueva plantilla
        </Button>
      </div>

      {!plantillas.length ? (
        <EstadoVacio
          icono="bi-palette2"
          titulo="Sin plantillas"
          descripcion="Crea una para reutilizar el asunto y el cuerpo de los correos que más envías."
          accion={{ texto: 'Nueva plantilla', onClick: () => setEditando('nueva') }}
        />
      ) : (
        <ul className="flex flex-col gap-3">
          {plantillas.map((t) => (
            <li key={t.id}>
              <Card>
                <CardHeader
                  titulo={
                    <span className="flex flex-wrap items-center gap-2">
                      {t.name}
                      <span className="text-2 font-normal text-muted">/{t.slug}</span>
                      {t.is_system
                        ? <Badge tone="warn">Sistema</Badge>
                        : <Badge tone="ok">Personalizada</Badge>}
                    </span>
                  }
                  accion={
                    <span className="flex gap-1.5">
                      {t.is_editable ? (
                        <Button variant="secondary" size="sm" onClick={() => setEditando(t)}>
                          <i className="bi bi-pencil" aria-hidden="true" /> Editar
                        </Button>
                      ) : (
                        <Badge tone="neutral">Solo lectura</Badge>
                      )}
                      {!t.is_system && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setBorrando(t)}
                          aria-label={`Eliminar la plantilla «${t.name}»`}
                          className="text-danger-ink"
                        >
                          <i className="bi bi-trash" aria-hidden="true" />
                        </Button>
                      )}
                    </span>
                  }
                />
                <CardBody className="text-2 text-muted">
                  <span className="font-semibold text-ink">Asunto: </span>
                  {t.subject_template || '—'}
                </CardBody>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {editando && (
        <FormularioDePlantilla
          plantilla={editando === 'nueva' ? null : editando}
          onCerrar={() => setEditando(null)}
        />
      )}

      <ConfirmDialog
        abierto={borrando != null}
        onCambio={(v) => !v && setBorrando(null)}
        titulo="Eliminar plantilla"
        descripcion={
          borrando
            ? `«${borrando.name}» se elimina para siempre. Los correos ya enviados no cambian.`
            : ''
        }
        textoConfirmar="Eliminar"
        destructivo
        cargando={borrar.isPending}
        onConfirmar={() =>
          borrando && borrar.mutate(borrando.id, {
            onSuccess: () => { setBorrando(null); Utils.toast('Plantilla eliminada', 'success'); },
            onError: (e: unknown) => {
              setBorrando(null);
              Utils.toast(e instanceof Error ? e.message : 'No se pudo eliminar', 'error');
            },
          })
        }
      />
    </div>
  );
}

function FormularioDePlantilla({
  plantilla, onCerrar,
}: { plantilla: Plantilla | null; onCerrar: () => void }) {
  const esNueva = plantilla == null;
  const [campos, setCampos] = useState<Campos>(
    plantilla
      ? {
        name: plantilla.name,
        subject_template: plantilla.subject_template ?? '',
        body_html_template: plantilla.body_html_template ?? '',
        body_push_template: plantilla.body_push_template ?? '',
      }
      : VACIA,
  );
  const [errores, setErrores] = useState<Record<string, string>>({});
  const crear = useCrearPlantilla();
  const guardar = useGuardarPlantilla();
  const ocupado = crear.isPending || guardar.isPending;

  const set = (k: keyof Campos) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setCampos((c) => ({ ...c, [k]: e.target.value }));
    setErrores((x) => ({ ...x, [k]: '' }));
  };

  function enviar() {
    const nuevos: Record<string, string> = {};
    // Las mismas tres obligatorias que exige el servidor al crear
    // (`TemplateBody` + `_nombre_no_vacio`).
    if (!campos.name.trim()) nuevos.name = 'La plantilla necesita un nombre.';
    else if (campos.name.trim().length > 100) nuevos.name = 'El nombre no puede pasar de 100 caracteres.';
    if (!campos.subject_template.trim()) nuevos.subject_template = 'Escribe el asunto del correo.';
    if (!campos.body_html_template.trim()) nuevos.body_html_template = 'Escribe el cuerpo del correo.';
    setErrores(nuevos);
    if (Object.keys(nuevos).length) return;

    const alTerminar = {
      onSuccess: () => {
        Utils.toast(esNueva ? 'Plantilla creada' : 'Plantilla guardada', 'success');
        onCerrar();
      },
      onError: (e: unknown) =>
        Utils.toast(e instanceof Error ? e.message : 'No se pudo guardar', 'error'),
    };
    if (esNueva) crear.mutate(campos, alTerminar);
    else guardar.mutate({ id: plantilla.id, ...campos }, alTerminar);
  }

  const restantes = TOPE_PUSH - campos.body_push_template.length;

  return (
    <Dialog
      abierto
      onCambio={(v) => !v && onCerrar()}
      titulo={esNueva ? 'Nueva plantilla' : `Editar: ${plantilla.name}`}
      descripcion="El asunto y el cuerpo admiten variables, que se sustituyen al enviar."
      ancho="max-w-2xl"
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar}>Cancelar</Button>
          <Button onClick={enviar} loading={ocupado}>
            {esNueva ? 'Crear plantilla' : 'Guardar'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Nombre de la plantilla" error={errores.name}>
          <Input
            value={campos.name}
            onChange={set('name')}
            maxLength={100}
            placeholder="Ej: Aviso de mantenimiento"
          />
        </Field>

        <Field label="Asunto del correo" error={errores.subject_template}>
          <Input
            value={campos.subject_template}
            onChange={set('subject_template')}
            placeholder="Asunto con {{variables}}"
          />
        </Field>

        <Field label="Cuerpo HTML" error={errores.body_html_template}>
          <Textarea
            rows={8}
            value={campos.body_html_template}
            onChange={set('body_html_template')}
            placeholder="<p>Hola {{nombre_corto}}…</p>"
            className="font-mono text-2"
          />
        </Field>

        <Field
          label="Texto push"
          opcional
          ayuda={`El servidor corta a ${TOPE_PUSH} caracteres.`}
          error={restantes < 0 ? `Te pasas por ${-restantes} caracteres.` : undefined}
        >
          <Input
            value={campos.body_push_template}
            onChange={set('body_push_template')}
            maxLength={TOPE_PUSH}
          />
        </Field>
        <p className="-mt-3 text-2 text-muted tabular-nums" aria-live="polite">
          {restantes} caracteres disponibles
        </p>

        <div className="rounded-sm border border-line bg-sunken px-3 py-2 text-2">
          <p className="mb-1 flex items-center gap-1.5 font-semibold text-ink">
            <i className="bi bi-braces" aria-hidden="true" /> Variables disponibles
          </p>
          <ul className="flex flex-wrap gap-1.5">
            {VARIABLES.map((v) => (
              <li key={v} className="rounded-xs bg-surface px-1.5 py-0.5 font-mono text-1 text-ink-2">
                {v}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Dialog>
  );
}
